import { ActionIcon, Button, Menu, Modal, Pagination, Select, TextInput } from '@mantine/core';
import {
  IconArrowBackUp,
  IconChevronDown,
  IconDownload,
  IconPaperclip,
  IconPlus,
  IconPrinter,
  IconReceipt,
  IconSearch,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { monthLabel } from '../payroll/view.js';
import { pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { OwnerAttachments } from '../pms/OwnerAttachments.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Cash.module.css';
import { CashAccounts } from './CashAccounts.js';
import {
  DecisionDialog,
  EntryDialog,
  ExchangeDialog,
  PayrollPostDialog,
  ReasonDialog,
  ReceiptDialog,
  ReconcileDialog,
  ReviewDialog,
  SubmitDialog,
  TransferDialog,
  useCashError,
} from './CashDialogs.js';
import {
  type CtmFormValue,
  CtmListPanel,
  CtmRequestPanel,
  CtmStatusPanel,
  inputOf,
  Panel,
} from './CtmBoard.js';
import { monthlyStatementPdf } from './monthly-pdf.js';
import type { CashSource } from './source.js';
import type { CashData, CashReconciliation, CashSection, CashTxn, CtmItem } from './types.js';
import {
  cashMonthOptions,
  ctmCsv,
  ctmQueue,
  formatMoney,
  ledgerCsv,
  localTimeOf,
  txnTone,
} from './view.js';

export interface CashScreenProps {
  source: CashSource;
  section: CashSection;
  /** Bugün (gemi yerel günü ya da ofiste UTC), `YYYY-MM-DD`. */
  today: string;
  /**
   * Derin bağlantı (ADR 0156): panodaki satırın gemisi ve kaydı. CTM bölümlerinde kayıt seçilir,
   * sayım bölümünde satır işaretlenir.
   */
  initial?: { vesselId: string | null; id: string | null };
}

/** Bağlantı sorgusu `?vessel=&id=` (panodaki iş satırı; ADR 0156 "Yollar"). */
export function cashLinkOf(search: string): { vesselId: string | null; id: string | null } {
  const params = new URLSearchParams(search);
  return { vesselId: params.get('vessel'), id: params.get('id') };
}

/** Menü yaprağının yolu (ADR 0093 §8). */
export const CASH_SECTION_PATHS: Record<CashSection, string> = {
  accounts: '/finance/accounts',
  'ctm-requests': '/finance/ctm',
  'ctm-approvals': '/finance/ctm/approvals',
  'ctm-receipts': '/finance/ctm/receipts',
  'ctm-history': '/finance/ctm/history',
  transactions: '/finance/cash',
  balances: '/finance/cash/balances',
  reconciliation: '/finance/cash/reconciliation',
  monthly: '/finance/cash/monthly',
  reports: '/finance/reports',
};

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: CashData };

type Dialog =
  | { kind: 'receipt'; item: CtmItem }
  | { kind: 'decide'; item: CtmItem; decision: 'approved' | 'returned' }
  | { kind: 'transfer'; item: CtmItem }
  | { kind: 'cancel'; item: CtmItem }
  | { kind: 'submit'; item: CtmItem }
  | { kind: 'entry'; type: 'opening' | 'receipt' | 'expense' }
  | { kind: 'exchange' }
  | { kind: 'reverse'; txn: CashTxn }
  | { kind: 'files'; txn: CashTxn }
  | {
      kind: 'reconcile';
      type: 'month_end' | 'handover' | 'takeover';
      currency: string;
      month: string;
      calculated: number;
      handover?: CashReconciliation;
    }
  | { kind: 'review'; record: CashReconciliation }
  | { kind: 'payroll' };

const PAGE = 10;

/**
 * Finance ▸ Cash to Master ve Cash Account (ADR 0093): ortağın kasa-ekran.jpeg düzeni. Kasayı
 * gemide kaptan tutar (talep, teyit, işlem, sayım); ofis salt okunur görür, CTM'ye karar verir,
 * ödemeyi düzenler ve sayımı inceler. Ekran gemi uygulamasında ve web ofiste aynıdır (ADR 0019 §2).
 */
export function CashScreen({ source, section, today, initial }: CashScreenProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const message = useCashError();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState<{ vesselId?: string; month?: string; currency?: string }>(
    () => (initial?.vesselId ? { vesselId: initial.vesselId } : {}),
  );
  const [selectedId, setSelectedId] = useState<string | null>(initial?.id ?? null);
  // Bağlantının kaydı yalnız ilk açılışta seçilir; bölüm değişince seçim sıfırlanır
  const shownSection = useRef(section);
  const [creating, setCreating] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Yalnız bölüm değişiminde
  useEffect(() => {
    if (shownSection.current === section) return;
    shownSection.current = section;
    setDialog(null);
    setCreating(false);
    setSelectedId(null);
    setNotice(null);
  }, [section]);

  const load = useCallback(async () => {
    try {
      const data = await source.load(query);
      setState({ kind: 'ready', data });
      return data;
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
      return null;
    }
  }, [source, query]);
  useEffect(() => {
    setState({ kind: 'loading' });
    void load();
  }, [load]);

  // Bağlantının sayım satırı bir kez görünür alana kaydırılır
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || state.kind !== 'ready' || !initial?.id) return;
    const row = document.querySelector('tr[aria-current="true"]');
    if (!row) return;
    scrolled.current = true;
    row.scrollIntoView({ block: 'center' });
  }, [state, initial?.id]);

  const data = state.kind === 'ready' ? state.data : null;
  const editor = source.editor;
  const office = source.office;
  const can = {
    create: Boolean(editor && data?.can.create),
    submit: Boolean(editor && data?.can.submit),
    approve: Boolean(editor && data?.can.approve),
    void: Boolean(editor && data?.can.void),
    export: Boolean(data?.can.export),
    payroll: Boolean(editor && data?.can.payroll && data?.can.approve),
    decide: Boolean(office && data?.can.decide),
    transfer: Boolean(office && data?.can.transfer),
    review: Boolean(office && data?.can.review),
  };

  const done = async (text: string) => {
    setDialog(null);
    setNotice({ tone: 'success', text });
    await load();
  };
  const fail = (error: unknown) => setNotice({ tone: 'danger', text: message(errorCode(error)) });

  const title = t(`cash.sections.${section}.title`);
  const currency = data?.currency ?? null;
  const month = data?.month ?? query.month ?? today.slice(0, 7);
  const header = (
    <PageHeader
      title={title}
      description={t(`cash.sections.${section}.description`)}
      aside={
        data ? (
          <div className={classes.selectors}>
            {data.vessels && data.vessels.length > 0 ? (
              <Select
                label={t('cash.vessel')}
                data={data.vessels.map((v) => ({
                  value: v.id,
                  label: v.pending > 0 ? `${v.name} (${v.pending})` : v.name,
                }))}
                value={data.vessel.id}
                allowDeselect={false}
                onChange={(v) =>
                  v && setQuery(({ currency: _currency, ...q }) => ({ ...q, vesselId: v }))
                }
              />
            ) : null}
            {section !== 'accounts' &&
            section !== 'ctm-requests' &&
            section !== 'ctm-approvals' &&
            section !== 'ctm-receipts' &&
            section !== 'ctm-history' ? (
              <>
                <Select
                  label={t('cash.account')}
                  data={data.currencies.map((c) => ({
                    value: c,
                    label: t('cash.accountName', { currency: c }),
                  }))}
                  value={currency}
                  allowDeselect={false}
                  disabled={data.currencies.length === 0}
                  onChange={(v) => v && setQuery((q) => ({ ...q, currency: v }))}
                />
                <Select
                  label={t('cash.month')}
                  data={cashMonthOptions(data.today).map((m) => ({
                    value: m,
                    label: monthLabel(m, language),
                  }))}
                  value={month}
                  allowDeselect={false}
                  onChange={(v) => {
                    if (!v) return;
                    setPage(1);
                    setQuery((q) => ({ ...q, month: v }));
                  }}
                />
              </>
            ) : null}
          </div>
        ) : undefined
      }
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        {state.code === 'FORBIDDEN' ? (
          <EmptyState tone="danger" title={t('cash.empty.noAccess')} />
        ) : (
          <LoadError title={t('cash.errors.load')} code={state.code} onRetry={() => void load()} />
        )}
      </div>
    );
  }
  if (!data) {
    return (
      <div className={classes.screen}>
        {header}
        <TableSkeleton columns={7} label={title} />
      </div>
    );
  }

  /* ---------- CTM ---------- */

  const queue = ctmQueue(data.ctm.items, section);
  const selected = creating
    ? null
    : (data.ctm.items.find((i) => i.id === selectedId) ?? queue[0] ?? null);

  const saveCtm = async (form: CtmFormValue, submit: boolean) => {
    const input = inputOf(form, language);
    if (!editor || !input) return;
    setBusy(true);
    try {
      const editing = !creating && selected ? selected : null;
      const { id } = await editor.saveCtm(
        editing ? { id: editing.id, rowVersion: editing.rowVersion, input } : { input },
      );
      setCreating(false);
      setSelectedId(id);
      const fresh = await load();
      const item = fresh?.ctm.items.find((i) => i.id === id);
      if (submit && item) setDialog({ kind: 'submit', item });
      else setNotice({ tone: 'success', text: t('cash.notices.saved', { no: item?.ctmNo ?? '' }) });
    } catch (error) {
      fail(error);
    }
    setBusy(false);
  };

  const ctmActions = (item: CtmItem | null) => {
    if (!item) return {};
    const actions: Partial<Record<'review' | 'arranged' | 'received' | 'created', ReactNode>> = {};
    if (can.decide && item.canDecide && item.status === 'pending') {
      actions.review = (
        <>
          <Button
            size="xs"
            onClick={() => setDialog({ kind: 'decide', item, decision: 'approved' })}
          >
            {t('cash.ctm.actions.approve')}
          </Button>
          <Button
            size="xs"
            variant="default"
            onClick={() => setDialog({ kind: 'decide', item, decision: 'returned' })}
          >
            {t('cash.ctm.actions.return')}
          </Button>
        </>
      );
    }
    if (can.transfer && item.status === 'approved') {
      actions.arranged = (
        <Button size="xs" onClick={() => setDialog({ kind: 'transfer', item })}>
          {t('cash.ctm.actions.arrange')}
        </Button>
      );
    }
    if (can.approve && (item.status === 'approved' || item.status === 'transferred')) {
      actions.received = (
        <Button size="xs" onClick={() => setDialog({ kind: 'receipt', item })}>
          {t('cash.ctm.actions.confirmReceipt')}
        </Button>
      );
    }
    if (can.void && item.status !== 'received' && item.status !== 'cancelled') {
      actions.created = (
        <Button
          size="xs"
          variant="subtle"
          color="red"
          onClick={() => setDialog({ kind: 'cancel', item })}
        >
          {t('cash.ctm.actions.cancel')}
        </Button>
      );
    }
    return actions;
  };

  const ctmBoard = (
    <div className={classes.ctmGrid}>
      <CtmRequestPanel
        key={`${creating ? 'new' : (selected?.id ?? 'none')}-${selected?.rowVersion ?? 0}`}
        data={data}
        item={selected}
        creating={creating}
        editable={can.create}
        busy={busy}
        onSave={(form, submit) => void saveCtm(form, submit)}
      />
      <CtmStatusPanel item={creating ? null : selected} actions={ctmActions(selected)} />
      <CtmListPanel
        data={data}
        section={section}
        selectedId={creating ? null : (selected?.id ?? null)}
        canCreate={can.create}
        onSelect={(id) => {
          setCreating(false);
          setSelectedId(id);
          setNotice(null);
        }}
        onNew={() => {
          setCreating(true);
          setNotice(null);
        }}
      />
    </div>
  );

  /* ---------- Kasa ---------- */

  const balanceRow = data.balances.find((b) => b.currency === currency) ?? null;
  const monthRec =
    data.reconciliations.find(
      (r) => r.kind === 'month_end' && r.currency === currency && r.month === month,
    ) ?? null;
  const ledger = [...data.transactions].reverse();
  const filtered = data.transactions.filter((txn) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [txn.txnNo, txn.payee, txn.remarks, txn.category, txn.linkedRef, txn.receiptNo]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const shown = filtered.slice((Math.min(page, pages) - 1) * PAGE, Math.min(page, pages) * PAGE);

  const description = (txn: CashTxn) =>
    txn.txnType === 'opening'
      ? t('cash.txn.descriptions.opening')
      : txn.txnType === 'reversal'
        ? t('cash.txn.descriptions.reversal', { no: txn.linkedRef ?? '' })
        : txn.txnType === 'ctm_received'
          ? `${t('cash.txn.descriptions.ctm_received')} · ${txn.linkedRef ?? ''}`
          : txn.txnType === 'adjustment'
            ? t('cash.txn.descriptions.adjustment')
            : (txn.payee ?? txn.remarks ?? '—');

  const brand = async () => (source.brand ? await source.brand() : null);
  const statementPdf = async (print: boolean) => {
    if (!source.pdf || !currency || !balanceRow) return;
    const brandValue = await brand();
    const render = () =>
      renderPdf(
        monthlyStatementPdf((key, options) => t(key, options ?? {}), {
          vessel: { name: data.vessel.name },
          brand: brandValue,
          currency,
          month,
          summary: balanceRow,
          rows: ledger,
          reconciliation: monthRec,
          printedAt: new Date().toISOString(),
          language,
        }),
        { print },
      );
    const name = `cash-statement-${currency}-${month}.pdf`;
    try {
      await (print ? source.pdf.print(name, render) : source.pdf.save(name, render));
    } catch (error) {
      setNotice({ tone: 'danger', text: t(pdfErrorKey(errorCode(error))) });
    }
  };
  const csv = async (name: string, text: string) => {
    const bytes = new TextEncoder().encode(`﻿${text}`);
    try {
      if (source.saveFile) await source.saveFile(name, bytes, 'text/csv');
      else {
        const url = URL.createObjectURL(new Blob([bytes], { type: 'text/csv' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = name;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      setNotice({ tone: 'success', text: t('cash.notices.file', { name }) });
    } catch (error) {
      fail(error);
    }
  };

  // Yoldaki para (ADR 0110 §3): bakiyeye girmez, kartta ayrı satır
  const inTransitOf = (c: string) => data.positions?.find((p) => p.currency === c)?.inTransit ?? 0;
  const balancesPanel = (
    <Panel index={5} title={t('cash.panels.balances')}>
      {data.balances.length === 0 ? (
        <p className={classes.note}>{t('cash.empty.noAccount')}</p>
      ) : (
        <div className={classes.balanceCards}>
          {data.balances.map((b) => (
            <div key={b.currency} className={classes.balanceCard}>
              <span className={classes.balanceHead}>
                <span className={classes.currencyIcon} aria-hidden>
                  {b.currency.slice(0, 2)}
                </span>
                {b.currency}
                {b.closed ? (
                  <StatusBadge tone="neutral">{t('cash.balance.closed')}</StatusBadge>
                ) : null}
              </span>
              <dl className={classes.balanceRows}>
                <dt>{t('cash.balance.opening')}</dt>
                <dd>{formatMoney(b.opening, language)}</dd>
                <dt>{t('cash.balance.in')}</dt>
                <dd>{formatMoney(b.received, language)}</dd>
                <dt>{t('cash.balance.out')}</dt>
                <dd>{formatMoney(b.paid, language)}</dd>
                {inTransitOf(b.currency) > 0 ? (
                  <>
                    <dt>{t('cash.accounts.inTransit')}</dt>
                    <dd>{formatMoney(inTransitOf(b.currency), language)}</dd>
                  </>
                ) : null}
              </dl>
              <span className={classes.balanceTotal} data-low={b.balance <= 0 || undefined}>
                <span>{t('cash.balance.balance')}</span>
                <span>{formatMoney(b.balance, language)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );

  const statementPanel = (
    <Panel
      index={6}
      title={t('cash.panels.statement', {
        currency: currency ?? '—',
        month: monthLabel(month, language),
      })}
      actions={
        source.pdf && can.export && balanceRow ? (
          <Button
            size="xs"
            variant="white"
            leftSection={<IconPrinter size={14} />}
            onClick={() => void statementPdf(true)}
          >
            {t('cash.statement.view')}
          </Button>
        ) : undefined
      }
    >
      {!balanceRow ? (
        <p className={classes.note}>{t('cash.empty.noAccount')}</p>
      ) : (
        <dl className={classes.statement}>
          {(
            [
              ['opening', formatMoney(balanceRow.opening, language)],
              ['counted', monthRec ? formatMoney(monthRec.counted, language) : '—'],
              ['received', formatMoney(balanceRow.received, language)],
              ['difference', monthRec ? formatMoney(monthRec.difference, language) : '—'],
              ['paid', formatMoney(balanceRow.paid, language)],
              [
                'status',
                monthRec ? (
                  <StatusBadge
                    key="status"
                    tone={monthRec.difference === 0 ? 'success' : 'warning'}
                  >
                    {t(
                      monthRec.difference === 0
                        ? 'cash.statement.balanced'
                        : 'cash.statement.notBalanced',
                    )}
                  </StatusBadge>
                ) : (
                  <StatusBadge key="status" tone="neutral">
                    {t('cash.statement.open')}
                  </StatusBadge>
                ),
              ],
              [
                'closing',
                formatMoney(balanceRow.opening + balanceRow.received - balanceRow.paid, language),
              ],
              [
                'masterApproval',
                monthRec ? (
                  <StatusBadge key="approval" tone="success">
                    {formatDay(monthRec.signedAt.slice(0, 10))}
                  </StatusBadge>
                ) : (
                  '—'
                ),
              ],
            ] as const
          ).map(([key, value]) => (
            <div
              key={key}
              className={classes.statementRow}
              data-total={key === 'closing' || undefined}
            >
              <dt>{t(`cash.statement.${key}`)}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Panel>
  );

  const newMenu =
    editor && (can.create || can.approve) ? (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <Button leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={14} />}>
            {t('cash.txn.new')}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {can.create ? (
            <Menu.Item onClick={() => setDialog({ kind: 'entry', type: 'expense' })}>
              {t('cash.txn.newExpense')}
            </Menu.Item>
          ) : null}
          {can.create ? (
            <Menu.Item onClick={() => setDialog({ kind: 'entry', type: 'receipt' })}>
              {t('cash.txn.newReceipt')}
            </Menu.Item>
          ) : null}
          {can.create ? (
            <Menu.Item onClick={() => setDialog({ kind: 'exchange' })}>
              {t('cash.txn.newExchange')}
            </Menu.Item>
          ) : null}
          {can.approve ? (
            <Menu.Item onClick={() => setDialog({ kind: 'entry', type: 'opening' })}>
              {t('cash.txn.newOpening')}
            </Menu.Item>
          ) : null}
          {can.payroll ? (
            <Menu.Item onClick={() => setDialog({ kind: 'payroll' })}>
              {t('cash.txn.fromPayroll')}
            </Menu.Item>
          ) : null}
        </Menu.Dropdown>
      </Menu>
    ) : null;

  const txnTable = (rows: readonly CashTxn[], actions: boolean) => (
    <section
      className={classes.tableWrap}
      aria-label={t('cash.tableOf', { name: t('cash.panels.transactions') })}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            <th scope="col">{t('cash.txn.columns.dateTime')}</th>
            <th scope="col">{t('cash.txn.columns.id')}</th>
            <th scope="col">{t('cash.txn.columns.type')}</th>
            <th scope="col">{t('cash.txn.columns.description')}</th>
            <th scope="col">{t('cash.txn.columns.category')}</th>
            <th scope="col" className={classes.numeric}>
              {t('cash.txn.columns.in', { currency })}
            </th>
            <th scope="col" className={classes.numeric}>
              {t('cash.txn.columns.out', { currency })}
            </th>
            <th scope="col" className={classes.numeric}>
              {t('cash.txn.columns.balance', { currency })}
            </th>
            <th scope="col">{t('cash.txn.columns.receipt')}</th>
            {actions ? <th scope="col">{t('cash.txn.columns.action')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((txn) => (
            <tr key={txn.id}>
              <td className={classes.nowrap}>
                {formatDay(txn.day)} {localTimeOf(txn)}
              </td>
              <td className={classes.nowrap}>{txn.txnNo}</td>
              <td>
                <StatusBadge tone={txnTone(txn.txnType)}>
                  {t(`cash.txn.types.${txn.txnType}`)}
                </StatusBadge>
              </td>
              <td>
                {description(txn)}
                {txn.reversedBy ? (
                  <span className={classes.sub}>
                    {t('cash.txn.reversedBy', { no: txn.reversedBy })}
                  </span>
                ) : null}
                {txn.exchangeRate ? (
                  <span className={classes.sub}>
                    {t('cash.txn.rate', { rate: txn.exchangeRate })}
                  </span>
                ) : null}
                {txn.linkedRef && txn.txnType !== 'reversal' && txn.txnType !== 'ctm_received' ? (
                  <span className={classes.sub}>
                    {t('cash.txn.linked', { ref: txn.linkedRef })}
                  </span>
                ) : null}
              </td>
              <td>{txn.category ? t(`cash.txn.categories.${txn.category}`) : ''}</td>
              <td className={`${classes.numeric} ${classes.in}`}>
                {txn.direction === 'in' ? formatMoney(txn.amount, language) : '-'}
              </td>
              <td className={`${classes.numeric} ${classes.out}`}>
                {txn.direction === 'out' ? formatMoney(txn.amount, language) : '-'}
              </td>
              <td className={`${classes.numeric} ${classes.strong}`}>
                {formatMoney(txn.balance, language)}
              </td>
              <td>
                <span className={classes.receiptCell}>
                  {txn.receiptNo ? (
                    <span title={txn.receiptNo} className={classes.nowrap}>
                      <IconReceipt size={16} aria-label={txn.receiptNo} />
                    </span>
                  ) : null}
                  {/* Makbuz dosyaları (ADR 0121 §2): taranmış makbuz ve talep belgesi */}
                  {source.attachments ? (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label={t('cash.files.open', { no: txn.txnNo })}
                      onClick={() => setDialog({ kind: 'files', txn })}
                    >
                      <IconPaperclip size={14} />
                    </ActionIcon>
                  ) : null}
                </span>
              </td>
              {actions ? (
                <td>
                  {can.void && !txn.reversedBy && txn.txnType !== 'reversal' ? (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      leftSection={<IconArrowBackUp size={14} />}
                      onClick={() => setDialog({ kind: 'reverse', txn })}
                      aria-label={`${t('cash.txn.reverse')} ${txn.txnNo}`}
                    >
                      {t('cash.txn.reverse')}
                    </Button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );

  const transactionsPanel = (
    <Panel index={4} title={t('cash.panels.transactions')}>
      <div className={classes.toolbar}>
        <TextInput
          aria-label={t('cash.search')}
          placeholder={t('cash.search')}
          leftSection={<IconSearch size={16} aria-hidden />}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          w={260}
        />
        <div className={classes.toolbarEnd}>{newMenu}</div>
      </div>
      {data.transactions.length === 0 ? (
        <p className={classes.note}>
          {data.currencies.length === 0 ? t('cash.empty.noAccount') : t('cash.txn.empty')}
        </p>
      ) : filtered.length === 0 ? (
        <p className={classes.note}>{t('cash.txn.noMatch')}</p>
      ) : (
        <>
          {txnTable(shown, true)}
          <div className={classes.pager}>
            <Pagination
              total={pages}
              value={Math.min(page, pages)}
              onChange={setPage}
              aria-label={t('cash.panels.transactions')}
              getControlProps={(control) => ({ 'aria-label': t(`materials.page.${control}`) })}
            />
            <span>
              {t('cash.txn.pageInfo', {
                from: (Math.min(page, pages) - 1) * PAGE + 1,
                to: Math.min(Math.min(page, pages) * PAGE, filtered.length),
                total: filtered.length,
              })}
            </span>
          </div>
        </>
      )}
    </Panel>
  );

  /* ---------- Mutabakat ---------- */

  const nextMonth = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, mo ?? 1, 1)).toISOString().slice(0, 7);
  };
  const lastDayOf = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    return `${m}-${String(new Date(Date.UTC(y ?? 1970, mo ?? 1, 0)).getUTCDate()).padStart(2, '0')}`;
  };
  const monthRows = data.currencies.flatMap((c) => {
    const first = data.firstMonths[c];
    if (!first) return [];
    const closed = new Set(data.closedMonths[c] ?? []);
    const rows: { currency: string; month: string; closed: boolean; closable: boolean }[] = [];
    let previousClosed = true;
    for (let m = first; m <= data.today.slice(0, 7); m = nextMonth(m)) {
      const isClosed = closed.has(m);
      rows.push({
        currency: c,
        month: m,
        closed: isClosed,
        closable: !isClosed && previousClosed && data.today >= lastDayOf(m),
      });
      previousClosed = isClosed;
    }
    return rows.reverse().slice(0, 6);
  });
  const openMonthEnd = async (row: { currency: string; month: string }) => {
    const fresh = await source.load({ ...query, currency: row.currency, month: row.month });
    const summary = fresh.balances.find((b) => b.currency === row.currency);
    setDialog({
      kind: 'reconcile',
      type: 'month_end',
      currency: row.currency,
      month: row.month,
      calculated: summary ? summary.opening + summary.received - summary.paid : 0,
    });
  };
  const handoverPending = data.reconciliations.filter(
    (r) =>
      r.kind === 'handover' &&
      r.handoverTo?.id === data.me &&
      !data.reconciliations.some((x) => x.kind === 'takeover' && x.handoverId === r.id),
  );

  const reconciliationContent = (
    <div className={classes.cashGrid}>
      <Panel
        title={t('cash.panels.records')}
        actions={
          can.approve && data.currencies.length > 0 ? (
            <Button
              size="xs"
              variant="white"
              onClick={() =>
                setDialog({
                  kind: 'reconcile',
                  type: 'handover',
                  currency: currency ?? data.currencies[0] ?? 'USD',
                  month: data.today.slice(0, 7),
                  calculated:
                    data.balances.find((b) => b.currency === (currency ?? data.currencies[0]))
                      ?.balance ?? 0,
                })
              }
            >
              {t('cash.reconcile.handover')}
            </Button>
          ) : undefined
        }
      >
        {handoverPending.map((h) => (
          <p key={h.id} className={classes.rule}>
            {t('cash.reconcile.takeoverOf', { name: h.signedBy })} · {h.currency}{' '}
            {formatMoney(h.counted, language)}{' '}
            <Button
              size="compact-xs"
              onClick={() =>
                setDialog({
                  kind: 'reconcile',
                  type: 'takeover',
                  currency: h.currency,
                  month: data.today.slice(0, 7),
                  calculated: data.balances.find((b) => b.currency === h.currency)?.balance ?? 0,
                  handover: h,
                })
              }
            >
              {t('cash.reconcile.takeover')}
            </Button>
          </p>
        ))}
        {data.reconciliations.length === 0 ? (
          <p className={classes.note}>{t('cash.reconcile.empty')}</p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={t('cash.tableOf', { name: t('cash.panels.records') })}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{t('cash.reconcile.columns.kind')}</th>
                  <th scope="col">{t('cash.reconcile.columns.currency')}</th>
                  <th scope="col">{t('cash.reconcile.columns.period')}</th>
                  <th scope="col" className={classes.numeric}>
                    {t('cash.reconcile.columns.calculated')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {t('cash.reconcile.columns.counted')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {t('cash.reconcile.columns.difference')}
                  </th>
                  <th scope="col">{t('cash.reconcile.columns.signedBy')}</th>
                  <th scope="col">{t('cash.reconcile.columns.office')}</th>
                </tr>
              </thead>
              <tbody>
                {data.reconciliations.map((r) => (
                  <tr
                    key={r.id}
                    // Panodaki satırın kaydı (ADR 0156): işaretli ve görünür
                    data-selected={r.id === initial?.id || undefined}
                    aria-current={r.id === initial?.id || undefined}
                  >
                    <td>
                      {t(`cash.reconcile.kinds.${r.kind}`)}
                      {r.handoverTo ? (
                        <span className={classes.sub}>→ {r.handoverTo.name}</span>
                      ) : null}
                    </td>
                    <td>{r.currency}</td>
                    <td className={classes.nowrap}>
                      {r.kind === 'month_end'
                        ? monthLabel(r.month, language)
                        : formatDay(r.countedOn)}
                    </td>
                    <td className={classes.numeric}>{formatMoney(r.calculated, language)}</td>
                    <td className={classes.numeric}>{formatMoney(r.counted, language)}</td>
                    <td className={classes.numeric}>
                      {formatMoney(r.difference, language)}
                      {r.differenceReason ? (
                        <span className={classes.sub}>{r.differenceReason}</span>
                      ) : null}
                    </td>
                    <td>
                      {r.signedBy}
                      <span className={classes.sub}>{formatDay(r.signedAt.slice(0, 10))}</span>
                    </td>
                    <td>
                      {r.review ? (
                        <StatusBadge
                          tone={r.review.decision === 'approved' ? 'success' : 'warning'}
                        >
                          {t(`cash.reconcile.review.${r.review.decision}`)}
                        </StatusBadge>
                      ) : can.review ? (
                        <Button
                          size="compact-xs"
                          onClick={() => setDialog({ kind: 'review', record: r })}
                        >
                          {t('cash.reconcile.reviewAction')}
                        </Button>
                      ) : (
                        <StatusBadge tone="neutral">{t('cash.reconcile.review.none')}</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </Panel>
      <Panel title={t('cash.panels.reconciliation')}>
        {monthRows.length === 0 ? (
          <p className={classes.note}>{t('cash.empty.noAccount')}</p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={t('cash.tableOf', { name: t('cash.panels.reconciliation') })}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{t('cash.reconcile.currency')}</th>
                  <th scope="col">{t('cash.reconcile.month')}</th>
                  <th scope="col">{t('cash.reconcile.status')}</th>
                </tr>
              </thead>
              <tbody>
                {monthRows.map((row) => (
                  <tr key={`${row.currency}-${row.month}`}>
                    <td>{row.currency}</td>
                    <td className={classes.nowrap}>{monthLabel(row.month, language)}</td>
                    <td>
                      {row.closed ? (
                        <StatusBadge tone="success">{t('cash.reconcile.closed')}</StatusBadge>
                      ) : row.closable && can.approve ? (
                        <Button size="compact-xs" onClick={() => void openMonthEnd(row)}>
                          {t('cash.reconcile.closeMonth')}
                        </Button>
                      ) : (
                        <StatusBadge tone={row.closable ? 'warning' : 'neutral'}>
                          {t(row.closable ? 'cash.reconcile.due' : 'cash.reconcile.open')}
                        </StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <p className={classes.note}>{t('cash.reconcile.lockNote')}</p>
      </Panel>
    </div>
  );

  /* ---------- Bölüm ---------- */

  const isCtm =
    section === 'ctm-requests' ||
    section === 'ctm-approvals' ||
    section === 'ctm-receipts' ||
    section === 'ctm-history';
  const content = isCtm ? (
    ctmBoard
  ) : section === 'accounts' ? (
    <CashAccounts
      data={data}
      source={source}
      onDone={async (text) => {
        setNotice({ tone: 'success', text });
        await load();
      }}
      onSelectVessel={(vesselId) =>
        setQuery(({ currency: _currency, ...q }) => ({ ...q, vesselId }))
      }
    />
  ) : section === 'transactions' ? (
    <div className={classes.cashGrid}>
      {transactionsPanel}
      <div className={classes.stack}>
        {balancesPanel}
        {statementPanel}
      </div>
    </div>
  ) : section === 'balances' ? (
    balancesPanel
  ) : section === 'reconciliation' ? (
    reconciliationContent
  ) : section === 'monthly' ? (
    <div className={classes.stack}>
      {statementPanel}
      <Panel
        title={t('cash.statement.ledger')}
        actions={
          source.pdf && can.export && balanceRow ? (
            <Button
              size="xs"
              variant="white"
              leftSection={<IconDownload size={14} />}
              onClick={() => void statementPdf(false)}
            >
              {t('cash.statement.save')}
            </Button>
          ) : undefined
        }
      >
        {ledger.length === 0 ? (
          <p className={classes.note}>{t('cash.txn.empty')}</p>
        ) : (
          txnTable(ledger, false)
        )}
      </Panel>
    </div>
  ) : (
    <div className={classes.reportCards}>
      {(
        [
          [
            'ledger',
            () => csv(`cash-book-${currency ?? 'none'}-${month}.csv`, ledgerCsv(data.transactions)),
            !currency,
          ],
          ['ctm', () => csv(`ctm-register-${data.vessel.name}.csv`, ctmCsv(data.ctm.items)), false],
          ['statement', () => statementPdf(false), !source.pdf || !balanceRow],
        ] as const
      ).map(([key, run, disabled]) => (
        <div key={key} className={classes.reportCard}>
          <h2 className={classes.reportTitle}>{t(`cash.reports.${key}.title`)}</h2>
          <p className={classes.note}>{t(`cash.reports.${key}.text`)}</p>
          <div>
            <Button
              leftSection={<IconDownload size={16} />}
              disabled={disabled || !can.export}
              onClick={() => void run()}
            >
              {t('cash.reports.download')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  /* ---------- Pencereler ---------- */

  const dialogNode = (() => {
    if (!dialog) return null;
    const close = () => setDialog(null);
    switch (dialog.kind) {
      case 'receipt':
        return (
          <ReceiptDialog
            item={dialog.item}
            today={data.today}
            onClose={close}
            onConfirm={async (input, pw) => {
              await editor?.receiveCtm(dialog.item.id, input, pw.password);
              await done(t('cash.notices.received'));
            }}
          />
        );
      case 'decide':
        return (
          <DecisionDialog
            item={dialog.item}
            decision={dialog.decision}
            today={data.today}
            onClose={close}
            onConfirm={async (reason, pw, transfer) => {
              const pending = dialog.item.pending;
              if (!pending) return;
              await office?.decide(dialog.item.id, {
                contentHash: dialog.item.contentHash,
                submissionId: pending.submissionId,
                step: pending.step,
                decision: dialog.decision,
                ...(reason ? { reason } : {}),
                ...pw,
                ...(transfer ? { transfer } : {}),
              });
              await done(
                t(
                  dialog.decision === 'approved'
                    ? 'cash.notices.approved'
                    : 'cash.notices.returned',
                ),
              );
            }}
          />
        );
      case 'transfer':
        return (
          <TransferDialog
            item={dialog.item}
            today={data.today}
            onClose={close}
            onConfirm={async (input) => {
              await office?.transfer(dialog.item.id, input);
              await done(t('cash.notices.transferred'));
            }}
          />
        );
      case 'cancel':
        return (
          <ReasonDialog
            title={t('cash.cancel.title', { no: dialog.item.ctmNo })}
            text={t('cash.cancel.text')}
            label={t('cash.cancel.reason')}
            confirmLabel={t('cash.cancel.confirm')}
            onClose={close}
            onConfirm={async (reason) => {
              await editor?.cancelCtm(dialog.item.id, dialog.item.rowVersion, reason);
              await done(t('cash.notices.cancelled'));
            }}
          />
        );
      case 'submit':
        return (
          <SubmitDialog
            item={dialog.item}
            onClose={close}
            onConfirm={async (pw) => {
              await editor?.submitCtm(dialog.item.id, dialog.item.contentHash, pw.password);
              await done(t('cash.notices.submitted'));
            }}
          />
        );
      case 'entry':
        return (
          <EntryDialog
            kind={dialog.type}
            data={data}
            onClose={close}
            onConfirm={async (input) => {
              await editor?.addEntry(input);
              await done(t('cash.notices.entry'));
            }}
          />
        );
      case 'exchange':
        return (
          <ExchangeDialog
            data={data}
            onClose={close}
            onConfirm={async (input) => {
              await editor?.addExchange(input);
              await done(t('cash.notices.exchange'));
            }}
          />
        );
      case 'reverse':
        return (
          <ReasonDialog
            title={t('cash.reverse.title', { no: dialog.txn.txnNo })}
            text={t('cash.reverse.text')}
            label={t('cash.reverse.reason')}
            confirmLabel={t('cash.reverse.confirm')}
            onClose={close}
            onConfirm={async (reason) => {
              await editor?.reverse(dialog.txn.id, reason);
              await done(t('cash.notices.reversed'));
            }}
          />
        );
      case 'files':
        return (
          <Modal opened onClose={close} title={t('cash.files.title', { no: dialog.txn.txnNo })}>
            <p className={classes.note}>{t('cash.files.text')}</p>
            {source.attachments ? (
              <OwnerAttachments
                ownerId={dialog.txn.id}
                list={source.attachments}
                upload={editor && can.create ? source.upload : undefined}
                href={source.attachmentUrl}
                emptyText={t('cash.files.empty')}
                addLabel={t('cash.files.add')}
                label={t('cash.files.title', { no: dialog.txn.txnNo })}
                onError={fail}
              />
            ) : null}
          </Modal>
        );
      case 'reconcile':
        return (
          <ReconcileDialog
            kind={dialog.type}
            currency={dialog.currency}
            month={dialog.month}
            monthLabel={monthLabel(dialog.month, language)}
            calculated={dialog.calculated}
            data={data}
            handover={dialog.handover}
            onClose={close}
            onConfirm={async (body, pw) => {
              await editor?.reconcile({ ...body, ...pw });
              await done(t('cash.notices.reconciled'));
            }}
          />
        );
      case 'review':
        return (
          <ReviewDialog
            title={t('cash.reconcile.reviewTitle', {
              kind: t(`cash.reconcile.kinds.${dialog.record.kind}`),
              period:
                dialog.record.kind === 'month_end'
                  ? monthLabel(dialog.record.month, language)
                  : formatDay(dialog.record.countedOn),
            })}
            onClose={close}
            onConfirm={async (decision, remarks) => {
              await office?.review(dialog.record.id, decision, remarks);
              await done(t('cash.notices.reviewed'));
            }}
          />
        );
      case 'payroll':
        return (
          <PayrollPostDialog
            items={data.payroll ?? []}
            onClose={close}
            onPost={async (item) => {
              await editor?.postPayroll(item.kind, item.id);
              await done(t('cash.notices.posted'));
            }}
          />
        );
    }
  })();

  return (
    <div className={classes.screen}>
      {header}
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {data.scope === 'web' && section !== 'accounts' ? (
        <p className={classes.note}>{t('cash.readOnlyOffice')}</p>
      ) : null}
      {content}
      {dialogNode}
    </div>
  );
}
